import { v4 as uuid } from 'uuid';
import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import { Licenses } from '@Constants/CommonConstants';
import { RXELEMENT_TEXTS } from '@Constants/ElementConstants';
import * as Spinners from '@Services/Spinners';
import StructureModel from '@Components/Results/structure/structure-model.service';
import { withStructureContext } from '@Components/Results/structure/StructureContext';
import history from '@AppRoutes/history';
import Auth from '@Services/Auth';
import ResultTracking from '@Services/ResultTracking';
import UserDatabases from '@Services/UserDatabases';
import { getMenuSynthesisItems, getNewHitsetByStructure } from '@Containers/FindPreparations/FindPreparationsHTTPService';
import { isNewSynthesisPlannerEnabled } from '@Pages/RetroSynthesis/predictiveUtils';
import HoverTooltip from '@Components/HoverTooltip';
import Breadcrumb from '@Services/Breadcrumb';
import AppStorage from '@Services/AppStorage';
import Similarity from '@Services/Similarity';
import findPreparationsService from '@Containers/FindPreparations/FindPreparationsService';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import SynthesisPlannerService from '@Services/SynthesisPlanner';
import { generateResultsPath } from '@AppRoutes/utils';
import noop from 'lodash/noop';

const spinnerId = `rx-structure-${new Date().valueOf()}`;

export const SYNTHESIS_OPTION_NAMES = {
  manual: 'manually',
  auto: 'auto',
};

export function isLabnetworkContent(databaseId) {
  const db = UserDatabases.getDatabaseById(databaseId);
  return db && db.prefix === 'LN';
}

export function isSigmaAldrichContent(databaseId) {
  const db = UserDatabases.getDatabaseById(databaseId);
  return db && db.prefix === 'SA';
}

export function isSynthesizeAvailable(structure) {
  const license = get(Auth.getUser(), 'details.license', null);
  const databaseId = get(structure, 'databaseId', null);
  const molStructure = get(structure, 'molImage.structure', null);

  return (
    (databaseId && license !== Licenses.ReaxysMedChem && !isLabnetworkContent(databaseId) && !isSigmaAldrichContent(databaseId)) ||
    (molStructure && isNewSynthesisPlannerEnabled())
  );
}

export function synthesize(structure, option) {
  if (option.name === SYNTHESIS_OPTION_NAMES.manual) {
    return findPreparationsService.goToSynthesisPreparations(structure, structure.databaseId, 'reactions');
  }

  return SynthesisPlannerService.synthesisProjectAction(structure, structure.databaseId);
}

export function findSimilar(structure) {
  Similarity.showModalForStructure(structure, structure?.molImage?.structure, structure.databaseId)
    .then((modalResult) => AppStorage.instance.set('historyOfVisitedDbSimilarity', [modalResult.selectedResult]))
    .catch(Logger.error);
}

export function isSynthesisOptionDisabled(option, structure) {
  const { name } = option;
  const { molImage, databaseId, details } = structure || {};
  switch (name) {
    case SYNTHESIS_OPTION_NAMES.auto:
      return !(molImage && molImage.structure);
    case SYNTHESIS_OPTION_NAMES.manual:
      return !(databaseId && details);
    default:
      return false;
  }
}

function StructureSynthesisComponent(props) {
  const [options, setOptions] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    // This request is being cached, so will fire just once
    const reloadOptions = () => {
      getMenuSynthesisItems()
        .then((optionsData) => setOptions(optionsData))
        .catch(Logger.error);
    };
    reloadOptions();
  }, []);

  const synthesizeAlternative = () => {
    const { structure, metadata } = props;

    const handleSimilarConfirm = () => {
      findSimilar(structure);
    };

    const handleHitsetResponse = (response) => {
      if (response.resultSize) {
        const stateParams = {
          databaseId: response.databaseId,
          context: response.context,
          resultName: response.resultName,
          hitSetKey: response.hitSetKey,
          queryId: response.queryId,
          searchId: metadata.searchId || uuid(),
          pageNumber: 1,
          sort: '',
        };

        Breadcrumb.resetBreadcrumbsForDB(response.databaseId);

        Breadcrumb.createBreadcrumb(stateParams, response.resultSize)
          .then(() => {
            const pathName = generateResultsPath(stateParams);
            history.push({
              pathname: pathName,
              state: stateParams,
            });
          })
          .catch(Logger.error);
      } else {
        NotifyDialogsService.openModalWindow({
          title: RXELEMENT_TEXTS.quickSearchSynthesisPlannerInfoDialogTitle,
          text: RXELEMENT_TEXTS.synthesizeSimilarAlternativeInfoDialogText,
          cancelText: RXELEMENT_TEXTS.cancel,
          confirmText: RXELEMENT_TEXTS.yes,
          type: 'info',
          backdrop: 'static',
          onConfirm: handleSimilarConfirm,
          trackingOptions: {
            component: 'Synthesis planner',
          },
        });
      }
    };

    NotifyDialogsService.openModalWindow({
      title: RXELEMENT_TEXTS.quickSearchSynthesisPlannerInfoDialogTitle,
      text: RXELEMENT_TEXTS.synthesizeAlternativeInfoDialogText,
      confirmText: RXELEMENT_TEXTS.quickSearchSynthesisPlannerInfoDialogButton,
      type: 'info',
      backdrop: 'static',
      onConfirm() {
        Spinners.activate(spinnerId);

        getNewHitsetByStructure(structure?.molImage?.structure)
          .then(handleHitsetResponse)
          .finally(() => {
            Spinners.terminate(spinnerId);
          })
          .catch(Logger.error);
      },
    });
  };

  const isSynthesizeButtonShown = () => {
    const { structure } = props;

    return isSynthesizeAvailable(structure);
  };

  const doSynthesize = (option) => {
    const { structure } = props;
    // if Retrosynthesis or database supports reactions (including Reaxys and CI)
    if (option.name === SYNTHESIS_OPTION_NAMES.auto || UserDatabases.supportsReaction(structure.databaseId)) {
      return synthesize(structure, option);
    }

    // Manual synthesis for DBs that don't support reactions - the selected structure will be searched as product in Reaxys
    return synthesizeAlternative();
  };

  const toggleMenu = (open) => {
    const { trackingContext } = props;
    setIsOpen(open);
    if (open) {
      ResultTracking.structureContentInteraction('synthesis plan', trackingContext);
    }
  };

  const getDropdown = () => {
    const { tooltipId, structure } = props;
    const tooltipLabel = 'Synthesis plan and preparations';
    return options ? (
      <Flyout
        className="structure-toolbar-options"
        isControlled
        isOpen={isOpen}
        flyout={
          <div className="u-els-nowrap">
            <div className="u-els-margin-bottom u-els-font-size-h4">Synthesize</div>
            <div className="els-btn-group-vertical" role="list">
              {options.map((option) => (
                <ButtonWithIcon
                  data-e2e="synthesis-option"
                  key={option.name}
                  sprite="ChevronRight"
                  type="link"
                  linkIconSize="xs"
                  className={cn('u-els-margin-top-1o4', 'u-els-margin-bottom-1o4', 'e2e-drop-item', `e2e-${kebabCase(option.title)}`)}
                  onClick={() => doSynthesize(option)}
                  disabled={isSynthesisOptionDisabled(option, structure)}>
                  {option.title}
                </ButtonWithIcon>
              ))}
            </div>
          </div>
        }
        shouldCloseOnESC
        showCloseButton
        onToggle={toggleMenu}
        placement="bottom-end"
        pointerType="primary">
        <HoverTooltip id={tooltipId} tooltip={tooltipLabel} tooltipPlace="top" theme="simple">
          <span id="synthesis-plan-button-description" className="sr-only">
            Find preparations or Create retrosynthesis plans.
          </span>
          <ButtonWithIcon
            data-e2e="synthesis-plan-button"
            sprite="Flask"
            onClick={noop}
            iconOnly
            type="borderless"
            aria-expanded={isOpen}
            aria-describedby="synthesis-plan-button-description">
            Create synthesis plan
          </ButtonWithIcon>
        </HoverTooltip>
      </Flyout>
    ) : null;
  };

  if (isSynthesizeButtonShown()) {
    return getDropdown();
  }
  return null;
}

StructureSynthesisComponent.propTypes = {
  structure: PropTypes.instanceOf(StructureModel),
  metadata: PropTypes.object,
  trackingContext: PropTypes.string,
  tooltipId: PropTypes.string,
};

const StructureSynthesisComponentWithContext = withStructureContext(StructureSynthesisComponent);

export default StructureSynthesisComponentWithContext;
